/**
 * useKnownSpeakerIndex — the connected voice-profile names, resolved the way the
 * backend resolves them, for speaker-token parsing.
 *
 * Two properties matter here and neither is optional:
 *
 * 1. **Backend-equivalent edge resolution.** `in_voice_profiles` is edge-fed, so
 *    the names the backend sees come from `edge_resolution.py` — edge order,
 *    `fromIndex` selection, multi-port flattening, edge values overriding a
 *    persisted field. Hand-rolling that in the console produces a superset (or
 *    subset) of the backend's list, and a name the backend will not have is a
 *    role ID it cannot resolve. So this goes through the generated
 *    `resolveYDocInputFromEdgeRefsByFeType`, which shares its per-port resolvers
 *    with the write path.
 * 2. **No render storm.** Subscribing to every node (`useNodes()`) would re-render
 *    each video node on any canvas change. The ReactFlow store selector returns a
 *    joined string, so React bails out unless the resolved names actually change.
 */

import { useMemo } from 'react'
import { useStore } from 'reactflow'

import {
  resolveYDocInputFromEdgeRefsByFeType,
  type YDocInputFlushEdgeRef,
  type YDocInputFlushNode,
  type YDocWriteRecord,
  type YDocWriteValue,
} from '../../../../generated/nodeDataTypes'
import { createNadDebug } from '../../../../utils/debugLog'
import { buildKnownSpeakerIndex, type SpeakerNameIndex } from './speakerToken'

const speakerLog = createNadDebug('dw:av-script-format')

// NUL: profile names legitimately contain spaces, commas and parens — that is
// the whole reason this module exists — so the key separator must be a byte no
// name can carry.
const NAME_SEPARATOR = '\u0000'

function isProfileRecord(value: YDocWriteValue): value is YDocWriteRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function nonBlankString(value: YDocWriteValue): value is string {
  return typeof value === 'string' && value.trim().length > 0
}

/** Mirror of `_voice_profile_names_from_raw` (video_generator/agent.py). */
function usableProfileNames(resolved: YDocWriteValue): string[] {
  if (!Array.isArray(resolved)) return []
  const names: string[] = []
  for (const item of resolved) {
    if (!isProfileRecord(item)) continue
    // A profile with no usable voice_id is not in the backend's voice map, so
    // its name must not participate in speaker matching either.
    if (!nonBlankString(item.name) || !nonBlankString(item.voice_id)) continue
    names.push(item.name)
  }
  return names
}

export interface VoiceProfileEdgeRef {
  source: string
  sourceHandle?: string | null
  data?: { toIndex?: number; fromIndex?: number } | undefined
}

export function useKnownSpeakerIndex(
  edges: readonly VoiceProfileEdgeRef[],
): SpeakerNameIndex {
  const edgeRefs = useMemo<YDocInputFlushEdgeRef[]>(
    () =>
      edges.map((edge) => ({
        source: edge.source,
        sourceHandle: edge.sourceHandle ?? undefined,
        toIndex: edge.data?.toIndex,
        fromIndex: edge.data?.fromIndex,
      })),
    [edges],
  )

  // Selector returns a primitive so the default Object.is comparison keeps the
  // component from re-rendering when unrelated nodes move or update.
  const namesKey = useStore((state) => {
    if (edgeRefs.length === 0) return ''
    const nodes: YDocInputFlushNode[] = []
    for (const [, node] of state.nodeInternals) {
      nodes.push({ id: node.id, type: node.type, data: node.data })
    }
    try {
      const resolved = resolveYDocInputFromEdgeRefsByFeType(
        'videoGenerator',
        'in_voice_profiles',
        nodes,
        edgeRefs,
      )
      return usableProfileNames(resolved).join(NAME_SEPARATOR)
    } catch (error) {
      // An unsupported source/handle pair, or a multi-output edge missing
      // fromIndex, throws by design in the generated resolver. Speaker parsing
      // must not take the canvas down with it — the legacy split is the correct
      // degradation — but a silent empty index looks exactly like "no profiles
      // connected", so say so.
      speakerLog({
        event: 'known-profile-resolution-failed',
        edgeCount: edgeRefs.length,
        error: error instanceof Error ? error.message : String(error),
      })
      return ''
    }
  })

  return useMemo(
    () => buildKnownSpeakerIndex(namesKey ? namesKey.split(NAME_SEPARATOR) : []),
    [namesKey],
  )
}
